import { useState } from 'react'
import { Box, Flex, Textarea, Spinner } from '@chakra-ui/react'
import { Text, VStack } from '@minetrace/ui'
import { useFormColors, FormSection, FormLabel, FileInput, DatePicker } from '@minetrace/ui/forms'
import { useT } from '@minetrace/ui'
import { LuSignature as FileSignature } from 'react-icons/lu'
import type { SupplierContractResponse } from '../../../api/models/response/proveedor'
import DocumentoDescarga from '../../../components/DocumentoDescarga'

interface Step2bContratoProps {
  mode: 'edit' | 'readonly'
  activeContract: SupplierContractResponse | null
  contractHistory: SupplierContractResponse[]
  onUpload: (file: File, contractDate?: string, notes?: string, expirationDate?: string) => Promise<void>
}

export default function Step2bContrato({ mode, activeContract, contractHistory, onUpload }: Step2bContratoProps) {
  const c = useFormColors()
  const { t } = useT()
  const isReadonly = mode === 'readonly'

  const [file, setFile] = useState<File | null>(null)
  const [contractDate, setContractDate] = useState('')
  const [hasExpiration, setHasExpiration] = useState(false)
  const [expirationDate, setExpirationDate] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)

  const today = new Date().toISOString().split('T')[0]
  const inputStyles = {
    bg: c.bgInput, border: 'none', borderRadius: 'xl', color: c.text,
    _placeholder: { color: c.textMuted }, _focus: { outline: 'none', ring: '1px', ringColor: c.accent },
  } as const

  const handleUpload = async () => {
    if (!file) return
    setBusy(true)
    try {
      await onUpload(file, contractDate || undefined, notes || undefined, hasExpiration ? expirationDate || undefined : undefined)
      setFile(null)
      setContractDate('')
      setHasExpiration(false)
      setExpirationDate('')
      setNotes('')
    } finally {
      setBusy(false)
    }
  }

  const renderContractRow = (contract: SupplierContractResponse, active: boolean) => (
    <Flex key={contract.id} p={4} bg={c.bgHover} borderRadius="xl" gap={4} alignItems="center" flexWrap="wrap">
      <Box flexShrink={0} color={active ? c.accent : c.textMuted}>
        <FileSignature size={20} />
      </Box>
      <Box flex={1} minW={0}>
        <Flex gap={2} alignItems="center" flexWrap="wrap">
          {contract.documentFile ? (
            <DocumentoDescarga
              caption={contract.contractDate ? `Contrato ${new Date(contract.contractDate).getFullYear()}` : 'Contrato'}
              storagePath={contract.documentFile.storagePath}
              fileName={contract.documentFile.originalFileName}
            />
          ) : (
            <Text fontSize="sm" color={c.textMuted}>{t('proveedores.wizard.step2b.noDocument')}</Text>
          )}
        </Flex>
        <Flex gap={3} mt={1} flexWrap="wrap">
          {contract.contractDate && (
            <Text fontSize="xs" color={c.textMuted}>{t('proveedores.wizard.step2b.dateLabel')}: {contract.contractDate}</Text>
          )}
          {contract.expirationDate && (
            <Text fontSize="xs" color={c.textMuted}>{t('proveedores.wizard.step2b.expirationShort')}: {contract.expirationDate}</Text>
          )}
          {contract.notes && (
            <Text fontSize="xs" color={c.textMuted}>{contract.notes}</Text>
          )}
        </Flex>
      </Box>
      {active && (
        <Box px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="bold" textTransform="uppercase" bg={`${c.accent}20`} color={c.accent}>
          {t('proveedores.wizard.step2b.activeBadge')}
        </Box>
      )}
    </Flex>
  )

  return (
    <FormSection icon={FileSignature} titleTid="proveedores.wizard.step2b.title" descriptionTid="proveedores.wizard.step2b.description">
      <VStack gap={5} alignItems="stretch">
        {/* Contrato activo */}
        {activeContract ? (
          renderContractRow(activeContract, true)
        ) : (
          <Box p={4} textAlign="center" border="1px dashed" borderColor="gray.200" borderRadius="xl">
            <Text fontSize="sm" color={c.textMuted}>{t('proveedores.wizard.step2b.emptyMessage')}</Text>
          </Box>
        )}

        {/* Carga de un nuevo contrato (reemplaza al activo) */}
        {!isReadonly && (
          <Box p={4} border="1px solid" borderColor={c.border} borderRadius="xl">
            <Text fontSize="sm" fontWeight="600" color={c.text} mb={3}>
              {activeContract ? t('proveedores.wizard.step2b.replaceTitle') : t('proveedores.wizard.step2b.uploadTitle')}
            </Text>

            <FileInput
              labelTid="proveedores.wizard.step2b.documentLabel"
              accept=".pdf"
              maxSizeMb={10}
              value={file}
              onChange={setFile}
            />

            <Box mt={3}>
              <FormLabel>{t('proveedores.wizard.step2b.dateLabel')}</FormLabel>
              <DatePicker maxDate={today} value={contractDate} onChange={setContractDate} />
            </Box>

            <Flex mt={3} alignItems="center" gap={3}>
              <Text fontSize="sm" color={c.textSecondary}>{t('proveedores.wizard.step2b.hasExpirationLabel')}</Text>
              <Box
                as="button"
                // `Box as="button"` renderiza un <button> sin atributo type, y el default
                // de HTML es "submit". Este toggle vive dentro del <form> del wizard, así
                // que sin esto un clic enviaba el paso y saltaba a Derechos Mineros sin
                // guardar el contrato.
                type="button"
                w="42px" h="24px" borderRadius="full" position="relative" flexShrink={0}
                bg={hasExpiration ? c.accent : c.border}
                cursor="pointer"
                transition="background 0.2s"
                onClick={() => {
                  setHasExpiration((prev) => {
                    if (prev) setExpirationDate('')
                    return !prev
                  })
                }}
              >
                <Box
                  position="absolute" top="2px" left={hasExpiration ? '20px' : '2px'}
                  w="20px" h="20px" borderRadius="full" bg="white"
                  transition="left 0.2s" boxShadow="sm"
                />
              </Box>
            </Flex>

            {hasExpiration && (
              <Box mt={3}>
                <FormLabel>{t('proveedores.wizard.step2b.expirationLabel')}</FormLabel>
                <DatePicker minDate={contractDate || undefined} value={expirationDate} onChange={setExpirationDate} />
              </Box>
            )}

            <Box mt={3}>
              <FormLabel>{t('proveedores.wizard.step2b.notesLabel')}</FormLabel>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} {...inputStyles} />
            </Box>

            <Flex mt={4} justifyContent="flex-end">
              <button
                type="button"
                disabled={!file || busy}
                onClick={handleUpload}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 1rem', borderRadius: '0.5rem', fontSize: '0.875rem', fontWeight: 600,
                  background: !file || busy ? c.bgInput : '#b4532a', color: !file || busy ? c.textMuted : 'white',
                  border: 'none', cursor: !file || busy ? 'not-allowed' : 'pointer',
                }}
              >
                {busy && <Spinner size="sm" />}
                {t('proveedores.wizard.step2b.uploadButton')}
              </button>
            </Flex>

            <Text fontSize="xs" color={c.textMuted} mt={2}>{t('proveedores.wizard.step2b.optionalHint')}</Text>
          </Box>
        )}

        {/* Historial */}
        {contractHistory.length > 0 && (
          <Box>
            <Text fontSize="sm" fontWeight="600" color={c.textSecondary} mb={2}>
              {t('proveedores.wizard.step2b.historyTitle')}
            </Text>
            <VStack gap={2} alignItems="stretch">
              {contractHistory.map((ct) => renderContractRow(ct, false))}
            </VStack>
          </Box>
        )}
      </VStack>
    </FormSection>
  )
}
